<script setup lang="ts">
import type { SpacingProp } from '@/types/Layout';
import type Carousel from '@/components/widgets/Carousel.vue';
import type { AffiliateConfig } from '~/utils/perfilOficialConfigs';
import { affiliateConfigs } from '~/utils/perfilOficialConfigs';

type DisplayMode = 'list' | 'slide';

type Props = {
  button?: { to: string; text: string };
  items?: { affiliateName: string; config: AffiliateConfig }[];
  title?: string;
  icon?: string;
  isLoading?: boolean;
  displayMode?: DisplayMode;
  /**
   * Items per row.
   * In order as number or number list
   *
   * itemsPerRow or [desktop, tablet, mobile]
   *
   * Ex: 8 or [8, 6, 4]
   */
  perRow?: SpacingProp;
  /**
   * Space between items.
   * In order as number or number list
   *
   * spaceBetweenItems or [desktop, tablet, mobile]
   *
   * Ex: 24 or [24, 16, 12]
   */
  spaceBetween?: SpacingProp;
  /**
   * Se true, carrega os dados internamente do affiliateConfigs
   * Se false ou undefined, usa a prop items
   */
  loadFromConfig?: boolean;
};

const {
  button,
  items: itemsProp,
  title,
  icon,
  displayMode = 'slide', // Default to slide mode for affiliates
  isLoading,
  perRow = [8, 6, 3], // Adjust itemsPerRow for desktop, tablet, mobile
  spaceBetween = [18, 14, 8],
  loadFromConfig = false,
} = defineProps<Props>();

// Pre-computado fora do computed para evitar recalculo desnecessário
const perfilOficialList = Object.entries(affiliateConfigs).map(
  ([affiliateName, config]) => ({
    affiliateName,
    config,
  }),
);

// Se loadFromConfig for true, carrega os dados internamente
const items = computed(() => {
  return loadFromConfig ? perfilOficialList : itemsProp;
});

const { isMobile, getSpacingMeasurementFromPropList } = useLayout();
const { getLoadingStrategy } = useImageLoading();

const carouselRef = ref<InstanceType<typeof Carousel> | null>(null);

const isSlideMode = computed<boolean>(() => {
  return displayMode === 'slide';
});

const spaceBetweenItems = computed<number>(() => {
  return getSpacingMeasurementFromPropList(spaceBetween, 16);
});

const spaceBetweenInPixels = computed<string>(() => {
  return spaceBetweenItems.value + 'px';
});

const itemsPerRow = computed<number>(() => {
  return getSpacingMeasurementFromPropList(perRow, 2);
});

const isSliderFadeVisible = computed<boolean>(() => {
  return isMobile.value && !!items.value && items.value.length > 3;
});

const isNavigationLeftButtonDisabled = computed<boolean>(() => {
  return !!carouselRef.value?.isNavigationLeftButtonDisabled;
});

const isNavigationRightButtonDisabled = computed<boolean>(() => {
  return !!carouselRef.value?.isNavigationRightButtonDisabled;
});

const isNavigationButtonsVisible = computed<boolean>(() => {
  return (
    isSlideMode.value &&
    !isMobile.value &&
    (!isNavigationLeftButtonDisabled.value ||
      !isNavigationRightButtonDisabled.value)
  );
});

// Este componente NÃO está no sistema de tracking above fold
// Portanto, SEMPRE usa lazy loading (tudo 0)
const itemsAboveFold = computed<number>(() => {
  return 0; // TUDO LAZY
});
</script>

<template>
  <div :class="$style.listGames">
    <template v-if="isLoading">
      <div
        v-if="title"
        :class="$style.title"
      >
        <BaseCommonSkeleton :class="$style.skeletonTitle" />
      </div>

      <div :class="[$style.items]">
        <BaseCommonSkeleton
          v-for="i in isSlideMode ? itemsPerRow : 24"
          :key="i"
          :class="$style.skeletonItem"
        />
      </div>
    </template>

    <template v-else>
      <div
        v-if="title || isSlideMode"
        :class="$style.header"
      >
        <div :class="$style.titleContainer">
          <div
            v-if="icon"
            class="w-[36px] h-[36px] lg:w-[45px] lg:h-[45px] flex-shrink-0 flex items-center justify-center"
          >
            <NuxtIcon
              class="text-[36px] lg:text-[45px] w-full h-full"
              :filled="true"
              :name="icon"
            />
          </div>
          <p
            v-if="title"
            :class="$style.titleText"
          >
            {{ title }}
          </p>
        </div>

        <NuxtLink
          v-if="button?.to"
          class="flex items-center justify-center ml-auto gap-1 px-2.5"
          no-prefetch
          :to="button.to"
        >
          <span class="text-[11px] md:px-2.5 text-white font-bold leading-4">{{
            button.text
          }}</span>
          <NuxtIcon
            v-if="isMobile"
            class="text-[15px] text-white"
            name="perfil-oficial/next"
          />
        </NuxtLink>

        <div :class="$style.actions">
          <div class="flex items-center justify-center">
            <button
              v-if="isNavigationButtonsVisible"
              class="flex items-center justify-center rounded-full text-white disabled:opacity-50 px-2.5"
              :disabled="isNavigationLeftButtonDisabled"
              @click="carouselRef?.scrollToLeft(300)"
            >
              <NuxtIcon
                class="text-[15px]"
                name="perfil-oficial/back"
              />
            </button>
            <button
              v-if="isNavigationButtonsVisible"
              class="flex items-center justify-center rounded-full bg-gray-700 text-white disabled:opacity-50 px-2.5"
              :disabled="isNavigationRightButtonDisabled"
              @click="carouselRef?.scrollToRight(300)"
            >
              <NuxtIcon
                class="text-[15px]"
                name="perfil-oficial/next"
              />
            </button>
          </div>
        </div>
      </div>

      <div
        v-if="isSlideMode"
        class="w-full perfil-oficial-carousel-wrapper"
      >
        <LazyWidgetsCarousel
          ref="carouselRef"
          :display="{
            itemsPerRow,
            spaceBetweenItems,
          }"
          :item-class="$style.carouselItem"
          :items="items || []"
          :slider-fade="{ right: isSliderFadeVisible }"
        >
          <template #item="{ item, index }">
            <PerfilOficialCard
              :affiliate-name="item.affiliateName"
              :config="item.config"
              :loading="getLoadingStrategy(index, itemsAboveFold)"
            />
          </template>
        </LazyWidgetsCarousel>
      </div>

      <div
        v-else
        :class="[$style.items]"
      >
        <PerfilOficialCard
          v-for="(affiliate, index) in items"
          :key="affiliate.affiliateName"
          :affiliate-name="affiliate.affiliateName"
          :class="$style.mediaItem"
          :config="affiliate.config"
          :loading="getLoadingStrategy(index, itemsAboveFold)"
        />
      </div>
    </template>
  </div>
</template>

<style module lang="postcss">
.header {
  @apply flex justify-between items-center [background:linear-gradient(270deg,rgba(255,255,255,0.05)_0%,rgba(255,255,255,0.00)_100%)] rounded-md mb-2.5 lg:mb-4;
}

.titleContainer {
  @apply flex items-center h-[31px] lg:h-[45px] justify-center gap-2.5 md:gap-2;
}

.titleText {
  @apply text-white text-[16px] lg:text-[20px] font-bold leading-4;
}

.actions {
  @apply flex items-center gap-3;
}

.listGames {
  @apply flex flex-col select-none w-full;
}

.items {
  @apply grid w-full;

  gap: v-bind(spaceBetweenInPixels);
  grid-template-columns: repeat(v-bind(itemsPerRow), minmax(0, 1fr));
}

.skeletonTitle {
  @apply h-[20px] max-w-[200px] flex-1;
}

.skeletonItem {
  @apply w-full h-auto aspect-[25/29];
}

/* Margin já aplicado no Carousel.vue */
</style>
